import React, { useEffect, useState } from 'react';
import { fetchStream } from '../../services/profileService';

type AvatarProps = {
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  name?: string;
  shape?: 'circle' | 'square';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  className?: string;
  imageClassName?: string;
  src?: string;
  alt?: string;
  onClick?: () => void;
};

const Avatar = (props: AvatarProps) => {
  const {
    size = 'md',
    color = 'primary',
    className = '',
    imageClassName = '',
    name,
    shape = 'circle',
    src,
    alt,
    onClick,
  } = props;

  const [url, setUrl] = useState<string | null>(null);

  const avatarStyles = {
    sm: `min-w-6 w-6 h-6 text-[0.75rem] leading-[1rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-sm'}`,
    md: `min-w-8 w-8 h-8 text-[1rem] leading-[1.5rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-md'}`,
    lg: `min-w-12 w-12 h-12 text-[1.125rem] leading-[1.75rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-lg'}`,
    xl: `min-w-12 w-12 h-12 text-[1.5rem] leading-[1.75rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-xl'}`,
    '2xl': `min-w-14 w-14 h-14 text-[1.75rem] leading-[2.125rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`,
    '3xl': `min-w-16 w-16 h-16 text-[2rem] leading-[2.375rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`,
    '4xl': `min-w-20 w-20 h-20 text-[3rem] leading-[3.625rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`,
    '5xl': `min-w-24 w-24 h-24 text-[3rem] leading-[3.625rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`,
    '6xl': `min-w-41 w-41 h-41 text-[4.6594rem] leading-[6.5225rem] ${shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`,
  };

  const colorStyles = {
    primary: 'bg-primary-25 text-primary-50',
    secondary: 'bg-gray-100 text-gray-400',
    success: 'bg-green-100 text-green-400',
    warning: 'bg-yellow-100 text-yellow-400',
    error: 'bg-red-100 text-red-400',
    info: 'bg-blue-100 text-blue-400',
  };

  const baseClassName =
    'flex items-center justify-center overflow-hidden relative font-medium font-heading';

  const computedClassName = `${baseClassName} ${avatarStyles[size]} ${colorStyles[color]} ${className}`;

  useEffect(() => {
    if (src && src.includes('amazonaws.com')) {
      async function getAvatar() {
        const response = await fetchStream({ url: src as string });
        if (response) {
          setUrl(response as unknown as string);
          return;
        }
      }
      getAvatar();
    } else if (src) {
      setUrl(src);
    } else {
      setUrl(null);
    }
  }, [src]);

  return (
    <div className={computedClassName} onClick={onClick}>
      {url ? (
        <img
          className={`w-full h-full absolute top-0 left-0 object-cover ${imageClassName}`}
          src={url}
          alt={alt}
          referrerPolicy='no-referrer'
        />
      ) : (
        <span className='font-heading'>
          {name?.substring(0, 1)?.toUpperCase()}
        </span>
      )}
    </div>
  );
};

export default Avatar;
